import React from 'react';
import {
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box
} from '@mui/material';
import EmptyState from '../EmptyState';
import { formatCurrency, formatNumber } from './formatters';

// Função auxiliar para formatar CNPJ/CPF
const formatCnpjCpf = (cnpjCpf?: string): string => {
  if (!cnpjCpf) return '';
  
  // Remove caracteres não numéricos
  const numbers = cnpjCpf.replace(/\D/g, '');
  
  if (numbers.length === 11) {
    // CPF: 000.000.000-00
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (numbers.length === 14) {
    // CNPJ: 00.000.000/0000-00
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  
  // Retorna o valor original se não for CPF nem CNPJ
  return cnpjCpf;
};

interface Partner {
  nome: string;
  cnpj_cpf?: string;
  municipio: string;
  uf: string;
  total_competencias: number;
  competencias_ativas: string;
  primeira_transacao: string;
  ultima_transacao: string;
}

interface Fornecedor extends Partner {
  total_compras: number;
  quantidade_comprada: number;
  icms_compras: number;
}

interface Cliente extends Partner {
  total_vendas: number;
  quantidade_vendida: number;
  icms_vendas: number;
}

interface PartnersData {
  fornecedores?: Fornecedor[];
  clientes?: Cliente[];
}

interface DrillDownPartnersSectionProps {
  parceiros_comerciais: PartnersData;
  title?: string;
  maxRows?: number;
}

const DrillDownPartnersSection: React.FC<DrillDownPartnersSectionProps> = ({ 
  parceiros_comerciais, 
  title = "Parceiros Comerciais",
  maxRows = 10
}) => {
  if (!parceiros_comerciais) {
    return null;
  }

  const { fornecedores = [], clientes = [] } = parceiros_comerciais;

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
        {title}
      </Typography>
      
      <Grid container spacing={2}>
        {/* Fornecedores */}
        {fornecedores.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Principais Fornecedores (até 10 registros)
              <Chip 
                label={`${formatNumber(fornecedores.length)} fornecedores`} 
                size="small" 
                sx={{ ml: 1 }} 
                color="success"
                variant="outlined"
              />
            </Typography>
            
            <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ width: '50px' }}><strong>#</strong></TableCell>
                    <TableCell><strong>Fornecedor</strong></TableCell>
                    <TableCell><strong>UF</strong></TableCell>
                    <TableCell align="right"><strong>Compras</strong></TableCell>
                    <TableCell align="right"><strong>ICMS</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fornecedores.slice(0, maxRows).map((fornecedor, index) => (
                    <TableRow key={index} hover>
                      <TableCell align="center" sx={{ width: '50px', fontWeight: 500, color: 'text.secondary' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight={500}
                            title={`Ativo em ${fornecedor.total_competencias} competência(s)\nPeríodos: ${fornecedor.competencias_ativas}\nRelacionamento: ${new Date(fornecedor.primeira_transacao).toLocaleDateString('pt-BR')} até ${new Date(fornecedor.ultima_transacao).toLocaleDateString('pt-BR')}`}
                            sx={{ cursor: 'help' }}
                          >
                            {fornecedor.nome}
                          </Typography>
                          {fornecedor.cnpj_cpf && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatCnpjCpf(fornecedor.cnpj_cpf)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {fornecedor.municipio} • {fornecedor.total_competencias} competência(s)
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={fornecedor.uf}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary.main" fontWeight={500}>
                          {formatCurrency(fornecedor.total_compras)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatNumber(fornecedor.quantidade_comprada)} un.
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight={500}>
                          {formatCurrency(fornecedor.icms_compras)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {/* Clientes */}
        {clientes.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Principais Clientes (até 10 registros)
              <Chip 
                label={`${formatNumber(clientes.length)} clientes`} 
                size="small" 
                sx={{ ml: 1 }} 
                color="warning"
                variant="outlined"
              />
            </Typography>
            
            <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ width: '50px' }}><strong>#</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>UF</strong></TableCell>
                    <TableCell align="right"><strong>Vendas</strong></TableCell>
                    <TableCell align="right"><strong>ICMS</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientes.slice(0, maxRows).map((cliente, index) => (
                    <TableRow key={index} hover>
                      <TableCell align="center" sx={{ width: '50px', fontWeight: 500, color: 'text.secondary' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight={500}
                            title={`Ativo em ${cliente.total_competencias} competência(s)\nPeríodos: ${cliente.competencias_ativas}\nRelacionamento: ${new Date(cliente.primeira_transacao).toLocaleDateString('pt-BR')} até ${new Date(cliente.ultima_transacao).toLocaleDateString('pt-BR')}`}
                            sx={{ cursor: 'help' }}
                          >
                            {cliente.nome}
                          </Typography>
                          {cliente.cnpj_cpf && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatCnpjCpf(cliente.cnpj_cpf)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {cliente.municipio} • {cliente.total_competencias} competência(s)
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={cliente.uf}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary.main" fontWeight={500}>
                          {formatCurrency(cliente.total_vendas)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatNumber(cliente.quantidade_vendida)} un.
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="error.main" fontWeight={500}>
                          {formatCurrency(cliente.icms_vendas)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {/* Mensagem quando não há clientes */}
        {fornecedores.length > 0 && clientes.length === 0 && (
          <Grid item xs={12} md={6}>
            <EmptyState
              icon="👥"
              title="Nenhum cliente identificado"
              description="Este produto não possui vendas registradas no período"
              height={200}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default DrillDownPartnersSection; 